import { useLayoutEffect, useRef, type RefObject } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { takeNavIntent } from './nav-intent';
import { applyPendingUpdate } from '@/lib/pending-update';

/** Phones and tablets, where the tab bar is: the desktop layout does not move. */
const MOTION_QUERY =
  '(max-width: 63.9375rem) and (prefers-reduced-motion: no-preference)';

const DURATION_MS = 220;
const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
/** Far enough to read as a push, short enough not to drag the eye across. */
const SLIDE_PX = 28;

/**
 * Per-page navigation behaviour of the shell's ONE scroll container.
 *
 * The shell no longer remounts between pages, so `<main>` kept whatever
 * scroll position the last page left: the next page opened halfway down.
 * This does what a phone app does instead:
 *
 * - Going to a new page starts it at the top.
 * - Going BACK returns to where you were on that page, so a long patient
 *   list does not lose your place every time you open and close a record.
 * - A change of query string only (a filter, a tab inside the page) leaves
 *   the scroll alone.
 *
 * And, on phones only, the new page slides in from the reading-order end
 * on a push, from the start on a back, and fades on a tab switch.
 */
export function useRouteMotion(
  mainRef: RefObject<HTMLElement | null>,
  contentRef: RefObject<HTMLElement | null>,
  isRTL: boolean,
) {
  const location = useLocation();
  const navType = useNavigationType();
  const positions = useRef(new Map<string, number>());
  const currentKey = useRef(location.key);
  const lastPath = useRef(location.pathname);

  // Remember the position continuously: by the time the effect below runs,
  // the next page is already in the DOM and the old scrollTop is gone.
  useLayoutEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    const onScroll = () =>
      positions.current.set(currentKey.current, main.scrollTop);
    main.addEventListener('scroll', onScroll, { passive: true });
    return () => main.removeEventListener('scroll', onScroll);
  }, [mainRef]);

  useLayoutEffect(() => {
    currentKey.current = location.key;
    // Read even when the path is unchanged (a tap on the tab you are on), so
    // the mark cannot linger and turn the next push into a fade.
    const intent = takeNavIntent();
    if (location.pathname === lastPath.current) return;
    lastPath.current = location.pathname;

    // A new release is waiting: this is the moment to take it, between pages,
    // rather than in the middle of a form. See lib/pending-update.ts.
    if (applyPendingUpdate()) return;

    const main = mainRef.current;
    const content = contentRef.current;
    let stopRestoring = () => {};
    if (main) {
      const target =
        navType === 'POP' ? (positions.current.get(location.key) ?? 0) : 0;
      main.scrollTop = target;
      if (content && target > 0 && main.scrollTop < target - 1) {
        stopRestoring = restoreWhenTallEnough(main, content, target);
      }
    }

    if (!content || !window.matchMedia(MOTION_QUERY).matches) {
      return stopRestoring;
    }

    const dir = isRTL ? -1 : 1;
    const from =
      intent === 'switch' || navType === 'REPLACE'
        ? 'translate3d(0, 0, 0)'
        : `translate3d(${(navType === 'POP' ? -SLIDE_PX : SLIDE_PX) * dir}px, 0, 0)`;
    content.animate(
      [
        { opacity: 0, transform: from },
        { opacity: 1, transform: 'translate3d(0, 0, 0)' },
      ],
      { duration: DURATION_MS, easing: EASE },
    );
    return stopRestoring;
  }, [location.key, location.pathname, navType, mainRef, contentRef, isRTL]);
}

/** Long enough for a cached list to render; short enough not to surprise. */
const RESTORE_WINDOW_MS = 1500;

/**
 * A page coming back often renders in steps (skeleton, then rows), so on the
 * first frame it is too short to scroll to where you were. Keep applying the
 * position as the content grows, until it fits, the window closes, or the
 * person touches the page, whose own scrolling then wins.
 */
function restoreWhenTallEnough(
  main: HTMLElement,
  content: HTMLElement,
  target: number,
) {
  const stop = () => {
    observer.disconnect();
    clearTimeout(timer);
    main.removeEventListener('touchstart', stop);
    main.removeEventListener('wheel', stop);
  };
  const observer = new ResizeObserver(() => {
    main.scrollTop = target;
    if (main.scrollTop >= target - 1) stop();
  });
  const timer = setTimeout(stop, RESTORE_WINDOW_MS);
  observer.observe(content);
  main.addEventListener('touchstart', stop, { passive: true });
  main.addEventListener('wheel', stop, { passive: true });
  return stop;
}
